import { createContext, useContext, useMemo, type ReactNode } from "react";
import { LgPhrase } from "../../../lg/LgPhrase";
import type { GlossaryEntry } from "../../base/MarkdownContent";
import { indexOfJapaneseWord } from "../../../lib/japanese-word-boundary";

/** 欄に出す 1 語。札の描き方は呼び出し側 (apps/client の用語集の札) が持つ */
export interface LessonGlossaryTerm {
  slug: string;
  term: string;
  summary: string;
  category?: string;
}

/**
 * 選ばれた語を札に並べる関数。用語集の頁と同じ札 (GlossaryTermCard) は apps/client にあるので、
 * client の root が Provider で差し込む。この欄は ResumeArea と TextLessonPanel の 2 か所から
 * 出るので、props で渡さず context にした。Provider が無い app (camp-app) は素のリンクの一覧
 */
export type LessonGlossaryTermsRenderer = (terms: LessonGlossaryTerm[]) => ReactNode;

const LessonGlossaryTermsRendererContext = createContext<LessonGlossaryTermsRenderer | null>(null);
export const LessonGlossaryTermsProvider = LessonGlossaryTermsRendererContext.Provider;

function PlainTermLinks({ terms }: { terms: LessonGlossaryTerm[] }) {
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {terms.map((t) => (
        <li key={t.slug}>
          <a
            href={`/glossary/${t.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group block rounded-lg border border-main bg-surface px-3 py-2 transition-colors hover:border-primary/40"
          >
            <span className="block font-noto font-semibold max-sm:text-[0.9375rem] text-sm text-main">{t.term}</span>
            {t.summary && (
              <span className="block break-keep [overflow-wrap:anywhere] font-noto max-sm:text-[0.9375rem] text-xs text-sub leading-relaxed mt-0.5"><LgPhrase>{t.summary}</LgPhrase></span>
            )}
          </a>
        </li>
      ))}
    </ul>
  );
}

export interface LessonGlossaryLinksProps {
  /** レッスン本文 (markdown)。複数の断片を渡すときは連結して渡す */
  content: string;
  /** 用語辞書。DB に登録があり slug を持つものだけが対象になる */
  glossary?: GlossaryEntry[];
  /** 表示する最大件数 */
  maxItems?: number;
}

/**
 * 読み手に見えない字を落とす。コード中の `db` や `join` まで拾うと、日本語の説明では
 * 一度も触れていない用語が「このレッスンに出てくる用語」に並ぶ。
 *
 * 図を差し込んだ本文には <style> や <script> が丸ごと入っている。CSS の
 * `display:none` が用語「None」に、`@media (max-width:640px)` が用語
 * 「max-width」に当たり、SQL の「データベースとは」に両方が並んでいた。
 * 要素の中身 (style と script) と、タグの属性 (class="none" など) も落とす
 */
function stripCode(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/~~~[\s\S]*?~~~/g, " ")
    .replace(/`[^`\n]*`/g, " ")
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<(style|script)\b[\s\S]*?<\/\1\s*>/gi, " ")
    .replace(/<\/?[a-z][a-z0-9-]*(?:\s[^>]*)?\/?>/gi, " ");
}

/** ASCII だけの語は単語境界で見る。"js" が "jsx" に、"api" が "apis" に当たるのを防ぐ */
const ASCII_ONLY = /^[\x20-\x7e]+$/;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function firstIndexOf(haystackLower: string, needle: string): number {
  const key = needle.toLowerCase();
  /*
  日本語は語の切れ目を見る。素の indexOf だと「アセット」の中の「セット」
  (Python の集合) を拾い、**本文で一度も説明していない語が一覧の先頭に
  並んでいた。** 規則は本文側の自動リンクと同じものを使う
  (lib/japanese-word-boundary.ts)
  */
  if (!ASCII_ONLY.test(key)) return indexOfJapaneseWord(haystackLower, key);

  // 英数字で始まる / 終わる語だけ境界を要求する。"ci/cd" や "pom.xml" のように
  // 記号で終わる語に境界を付けると、直後が空白でもマッチしなくなる。
  //
  // 境界にはアンダースコアとハイフンも含める。含めないと、"cd" が
  // "user_cd" や "cd-rom" の一部に当たって、本文で一度も説明していない語が
  // 「このレッスンに出てくる用語」に並ぶ
  const head = /^[a-z0-9]/.test(key) ? "(?<![a-z0-9_-])" : "";
  const tail = /[a-z0-9]$/.test(key) ? "(?![a-z0-9_-])" : "";
  const m = new RegExp(`${head}${escapeRegExp(key)}${tail}`).exec(haystackLower);
  return m ? m.index : -1;
}

/**
 * 本文に出てくる順に、欄に出す語を選ぶ。欄 (LessonGlossaryLinks) と、札の難易度を引くレッスンの loader が
 * 同じ選び方をするために外へ出している
 */
export function pickLessonGlossaryTerms(
  content: string | null | undefined,
  glossary: readonly GlossaryEntry[] | undefined,
  maxItems = 8,
): LessonGlossaryTerm[] {
  if (!content || !glossary || glossary.length === 0) return [];

  const body = stripCode(content).toLowerCase();
  const bySlug = new Map<
    string,
    { slug: string; term: string; summary: string; category?: string; at: number }
  >();

  for (const entry of glossary) {
    if (!entry.slug) continue;

    // term と alias のうち、本文で最初に出てくる位置が最も早いものを採る
    let bestAt = -1;
    for (const cand of [entry.term, ...(entry.aliases ?? [])]) {
      if (!cand) continue;
      // 日本語は 2 文字で十分意味を持つが ("配列")、ASCII の 2 文字
      // ("cd" "cp" "ci") は識別子の一部に当たりやすいわりに、本文で
      // その語を説明していることが少ない
      const minLength = ASCII_ONLY.test(cand) ? 3 : 2;
      if (cand.length < minLength) continue;
      const at = firstIndexOf(body, cand);
      if (at === -1) continue;
      if (bestAt === -1 || at < bestAt) bestAt = at;
    }
    if (bestAt === -1) continue;

    const prev = bySlug.get(entry.slug);
    if (prev && prev.at <= bestAt) continue;
    bySlug.set(entry.slug, {
      slug: entry.slug,
      term: entry.term,
      summary: entry.summary ?? "",
      category: entry.category,
      at: bestAt,
    });
  }

  return Array.from(bySlug.values())
    .sort((a, b) => a.at - b.at)
    .slice(0, maxItems)
    .map(({ at: _at, ...term }) => term);
}

/**
 * 本文の末尾に置く「このレッスンに出てくる用語」。
 *
 * 用語ツールチップは前からあるが、あれは hydration 後にしか組み立たない
 * クライアント専用の仕掛けなので、配信される HTML には /glossary への
 * リンクが 1 本も入らない。実際に本番のレッスンページを引くと 0 件で、
 * 用語集ページは 1400 本あるレッスンから一度も参照されていない状態だった。
 *
 * ここは折りたたまず素のリンクで出す。読み手にとっても、本文で流した語を
 * 後からまとめて引き直せる場所になる。
 *
 * 別タブで開く。読んでいる途中で語の意味を引くための場所なので、
 * 同じタブで飛ばすとレッスンから離れてしまい、戻ってくるのに
 * ブラウザの戻るが要る。SPA 遷移も同じ理由で使わない。
 */
export function LessonGlossaryLinks({
  content,
  glossary,
  maxItems = 8,
}: LessonGlossaryLinksProps) {
  const renderTerms = useContext(LessonGlossaryTermsRendererContext);
  const terms = useMemo(
    () => pickLessonGlossaryTerms(content, glossary, maxItems),
    [content, glossary, maxItems],
  );

  // 1〜2 件だと見出しの割に中身が薄い。3 件から出す
  if (terms.length < 3) return null;

  /*
  最初から開いた姿で出す。畳むと、レッスンの下に用語があること自体が
  見えなくなっていた。長くなりすぎないよう、出す語は maxItems (既定 8) まで。
  /glossary への内部リンクは SSR の HTML に残る
  */
  return (
    <aside
      data-testid="lesson-glossary-links"
      aria-labelledby="lesson-glossary-heading"
      className="mt-10 mb-10 pt-8 border-t border-main"
    >
      <div className="flex items-baseline gap-2">
        <h2
          id="lesson-glossary-heading"
          className="font-noto text-lg md:text-xl font-bold text-main"
        >
          このレッスンに出てくる用語
        </h2>
        <span className="font-noto max-sm:text-[0.9375rem] text-sm text-sub">{terms.length} 語</span>
      </div>
      {/* 文節の塊で折る。390px で最後の行が「す。」だけになっていた */}
      <p className="mt-1 mb-4 font-noto max-sm:text-[0.9375rem] text-sm text-sub">
        <span className="inline-block">意味があいまいなまま進んだ語は、</span>
        <span className="inline-block">ここから読み直せます。</span>
      </p>
      {renderTerms ? renderTerms(terms) : <PlainTermLinks terms={terms} />}
    </aside>
  );
}
